import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { EmojiPicker } from 'frimousse'
import {
  Apple,
  Clock,
  Flag,
  Hash,
  Lightbulb,
  Plane,
  Search,
  Shuffle,
  Smile,
  Trash2,
  Trees,
  Users,
  Volleyball,
  icons,
} from 'lucide-react'
import { REWARDS, REWARD_CATEGORIES, getRewardIconUrl, rewardName } from '../../../lib/data/rewardCatalog'
import { formatProjectIcon, parseProjectIcon, type ProjectIconKind } from '../../../lib/projectIcon/projectIcon'
import { emojiIconColor, happyIconColor } from '../../../lib/projectIcon/iconColor'
import {
  getLucideColor,
  getRecentIcons,
  getSkinTone,
  pushRecentIcon,
  setLucideColor,
  setSkinTone,
  type SkinTone,
} from '../../../lib/projectIcon/recentIcons'
import { TITLE_PICKER_COLORS } from '../../../lib/projectIcon/titlePalette'
import { ColorDrop } from '../../icons/ColorDrop'
import { RewardCategoryIcon } from '../../icons/RewardCategoryIcon'
import { ProjectIcon } from '../ProjectIcon'
import { WithTooltip } from '../../ui/tooltip'
import { resolveBundledEmojiData, withRecentEmojis } from './emojiData'
import './IconPicker.scss'

export interface IconPickerValue {
  /** `happy:…`, `emoji:…` oder `lucide:…`. */
  icon: string
  /** Eine der 16 Titel-Farben oder `null` (= Ton des Lebensbereichs). */
  iconColor: string | null
}

export interface IconPickerProps {
  /** Das aktuelle Projekt-Icon (`Project.icon`); bestimmt den Tab beim Öffnen. */
  value: string | null | undefined
  /**
   * Gewählt — ein Icon oder eine Farbe fürs gewählte Lucide-Icon (#246) —, oder `null` für „Entfernen“. Eine
   * Wahl wirkt sofort; die Hülle bleibt dafür offen, damit man weiter probieren kann. Happy Emojis und Emojis
   * tragen ihre eigene Farbe — dort merkt sich ein Farbklick die Farbe nur fürs nächste Lucide-Icon.
   */
  onValueChange: (next: IconPickerValue | null) => void
  /**
   * **Vorschau** (#246): eine Farbe unter dem Zeiger — so sähe das gewählte Lucide-Icon aus. `undefined` =
   * Vorschau vorbei. Gespeichert wird erst beim Klick. Icons zeigen keine Vorschau: Ihr Klick lässt den Wähler
   * offen und lässt sich so gleich wieder ändern.
   */
  onPreview?: (preview: IconPickerValue | undefined) => void
  className?: string
}

const TABS: readonly ProjectIconKind[] = ['happy', 'emoji', 'lucide']

/** Die Gruppen von Emojibase in ihrer Reihenfolge (ohne „component“). */
const EMOJI_CATEGORIES = [
  { key: 'smileys', Icon: Smile },
  { key: 'people', Icon: Users },
  { key: 'nature', Icon: Trees },
  { key: 'food', Icon: Apple },
  { key: 'travel', Icon: Plane },
  { key: 'activities', Icon: Volleyball },
  { key: 'objects', Icon: Lightbulb },
  { key: 'symbols', Icon: Hash },
  { key: 'flags', Icon: Flag },
] as const

/** Die Hand in den sechs Hauttönen — der Knopf zeigt den gewählten und schaltet weiter. */
const SKIN_TONES: readonly { tone: SkinTone; hand: string }[] = [
  { tone: 'none', hand: '✋' },
  { tone: 'light', hand: '✋🏻' },
  { tone: 'medium-light', hand: '✋🏼' },
  { tone: 'medium', hand: '✋🏽' },
  { tone: 'medium-dark', hand: '✋🏾' },
  { tone: 'dark', hand: '✋🏿' },
]

const LUCIDE_NAMES = Object.keys(icons)
/** Lucide-Icons werden in Häppchen gezeichnet — alle ~1.600 auf einmal ruckeln beim Öffnen. */
const LUCIDE_CHUNK = 240
const EMOJI_COLUMNS = 9

/** Gleiches Emoji, auch in anderem Hautton (Hautton-Modifikatoren und Varianten-Selektor weg). */
const baseEmoji = (emoji: string) => emoji.replace(/\u{1F3FB}|\u{1F3FC}|\u{1F3FD}|\u{1F3FE}|\u{1F3FF}|\uFE0F/gu, '')
const sameEmoji = (a: string, b: string) => baseEmoji(a) === baseEmoji(b)

/**
 * Wo ein Emoji in frimousses Liste steht (px ab oben) — dieselbe Einteilung
 * wie dort: Kategorien in Reihenfolge, je ein Kopf, dann Reihen zu `columns`.
 * „Zuletzt benutzt“ zählt mit, gezielt wird auf die eigentliche Kategorie.
 */
export function emojiOffset(
  data: { categories: { index: number }[]; emojis: { emoji: string; category: number; skins?: Record<string, string> }[] },
  target: string,
  columns: number,
  rowHeight: number,
  headerHeight: number,
): number | null {
  let top = 0
  for (const category of data.categories) {
    const members = data.emojis.filter(e => e.category === category.index)
    if (!members.length) continue
    const index = category.index < 0 ? -1 : members.findIndex(e => sameEmoji(e.emoji, target))
    if (index >= 0) return top + headerHeight + Math.floor(index / columns) * rowHeight
    top += headerHeight + Math.ceil(members.length / columns) * rowHeight
  }
  return null
}

const normalize = (text: string) => text.toLocaleLowerCase().trim()
const splitCamel = (name: string) => name.replace(/([a-z0-9])([A-Z])/g, '$1 $2').toLowerCase()

/**
 * Der **Icon-Wähler** (CONTEXT.md „Projekt-Kopf“) — nur der Inhalt; die
 * Hülle (Radix-Popover) bringt der Aufrufer mit.
 *
 * Drei Tabs mit je eigenem Set: **Happy Emojis** (die Belohnungs-Grafiken des
 * Katalogs, vorerst), **Emojis** (Systemschrift, über frimousse) und
 * **Icons** (Lucide, einfarbig in einer Titel-Farbe). Darüber Filter und
 * Zufall, darunter die Kategorie-Leiste; oben in jedem Tab „Zuletzt benutzt“
 * (pro Gerät).
 *
 * @example
 * <PopoverContent align="start">
 *   <IconPicker value={project.icon} onValueChange={next => save(next)} />
 * </PopoverContent>
 */
export const IconPicker: React.FC<IconPickerProps> = ({ value, onValueChange, onPreview, className }) => {
  const { t, i18n } = useTranslation()
  const language = i18n.language?.startsWith('en') ? 'en' : 'de'
  const [tab, setTab] = useState<ProjectIconKind>(() => parseProjectIcon(value)?.kind ?? 'happy')
  const [query, setQuery] = useState('')
  const [skinTone, setSkinToneState] = useState<SkinTone>(getSkinTone)
  const [lucideColor, setLucideColorState] = useState<string | null>(getLucideColor)
  const [colorsOpen, setColorsOpen] = useState(false)
  /*
    „Zuletzt benutzt“ wird im selben Render gelesen, in dem der Tab wechselt —
    nicht erst per Effekt danach (gemeldet 2026-09-26: im Emoji-Tab blieb die
    Zeile leer). frimousse lädt seine Daten nur einmal; den ersten Render mit
    der Liste des vorigen Tabs holte es nie wieder ein.
  */
  const [recentVersion, setRecentVersion] = useState(0)
  // eslint-disable-next-line react-hooks/exhaustive-deps -- `recentVersion` liest den Speicher nach einer Wahl neu
  const recent = useMemo(() => getRecentIcons(tab), [tab, recentVersion])
  const searchRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    setQuery('')
    setColorsOpen(false)
  }, [tab])

  // Färben kann man nur ein schon gewähltes Lucide-Icon. Die Liste zeigt die überfahrene Farbe schon.
  const colorsTarget = parseProjectIcon(value)?.kind === 'lucide' ? value! : null
  const [hoverColor, setHoverColor] = useState<string | null | undefined>(undefined)
  const shownLucideColor = hoverColor !== undefined ? hoverColor : lucideColor
  // Geht der Wähler (oder gehen die Farben) zu, während der Zeiger noch darüber steht, ist die Vorschau vorbei.
  const previewRef = useRef(onPreview)
  previewRef.current = onPreview
  useEffect(() => () => previewRef.current?.(undefined), [])
  useEffect(() => {
    if (!colorsOpen) return
    return () => {
      setHoverColor(undefined)
      previewRef.current?.(undefined)
    }
  }, [colorsOpen])

  const choose = useCallback((icon: string) => {
    const parsed = parseProjectIcon(icon)
    if (!parsed) return
    const iconColor = parsed.kind === 'happy'
      ? happyIconColor(parsed.value)
      : parsed.kind === 'emoji'
        ? emojiIconColor(parsed.value)
        : lucideColor
    pushRecentIcon(parsed.kind, icon)
    setRecentVersion(v => v + 1)
    onValueChange({ icon, iconColor })
  }, [lucideColor, onValueChange])

  // ── Happy Emojis ─────────────────────────────────────────────────────
  const happySections = useMemo(() => {
    const q = normalize(query)
    const matches = REWARDS.filter(r => getRewardIconUrl(r.id) && (!q
      || [r.nameDe, r.nameEn, r.ctaDe, r.ctaEn].some(text => text && normalize(text).includes(q))))
    return REWARD_CATEGORIES
      .map(category => ({
        id: category.id,
        slug: category.slug,
        label: language === 'en' ? category.nameEn : category.nameDe,
        items: matches.filter(r => r.categoryId === category.id).map(r => ({
          icon: formatProjectIcon({ kind: 'happy', value: r.id }),
          label: rewardName(r, language),
        })),
      }))
      .filter(section => section.items.length > 0)
  }, [query, language])

  // ── Lucide ──────────────────────────────────────────────────────────
  const lucideMatches = useMemo(() => {
    const q = normalize(query)
    return q ? LUCIDE_NAMES.filter(name => splitCamel(name).includes(q) || name.toLowerCase().includes(q.replace(/\s+/g, ''))) : LUCIDE_NAMES
  }, [query])
  // Das gewählte Icon muss gezeichnet sein, damit hingescrollt werden kann —
  // die Häppchen reichen deshalb anfangs bis hinter es.
  const valueIndex = useMemo(() => {
    const parsed = parseProjectIcon(value)
    return parsed?.kind === 'lucide' && !query ? lucideMatches.indexOf(parsed.value) : -1
  }, [value, query, lucideMatches])
  const firstLimit = Math.max(LUCIDE_CHUNK, valueIndex + LUCIDE_CHUNK)
  const [lucideLimit, setLucideLimit] = useState(firstLimit)
  // eslint-disable-next-line react-hooks/exhaustive-deps -- nur bei Suche und Tab neu, nicht bei jeder Wahl
  useEffect(() => setLucideLimit(firstLimit), [query, tab])

  const shuffle = () => {
    let pool: string[] = []
    if (tab === 'happy') pool = happySections.flatMap(s => s.items.map(i => i.icon))
    if (tab === 'lucide') pool = lucideMatches.map(name => formatProjectIcon({ kind: 'lucide', value: name }))
    if (tab === 'emoji') {
      const cells = Array.from(bodyRef.current?.querySelectorAll<HTMLElement>('[frimousse-emoji]') ?? [])
      pool = cells.map(cell => cell.textContent ?? '').filter(Boolean).map(e => formatProjectIcon({ kind: 'emoji', value: e }))
    }
    if (pool.length) choose(pool[Math.floor(Math.random() * pool.length)])
  }

  // ── Kategorie-Leiste mit Scroll-Spy ─────────────────────────────────
  const bodyRef = useRef<HTMLDivElement>(null)
  const [activeCategory, setActiveCategory] = useState(0)

  const categoryAnchors = useCallback((): HTMLElement[] => {
    const root = bodyRef.current
    if (!root) return []
    return Array.from(root.querySelectorAll<HTMLElement>(tab === 'emoji' ? '[frimousse-category]' : '[data-picker-section]'))
  }, [tab])

  const scroller = useCallback((): HTMLElement | null => {
    const root = bodyRef.current
    if (!root) return null
    return tab === 'emoji' ? root.querySelector<HTMLElement>('[frimousse-viewport]') : root
  }, [tab])

  const jumpToCategory = (index: number) => {
    const target = categoryAnchors()[index]
    const scroll = scroller()
    if (!target || !scroll) return
    scroll.scrollTo({ top: target.offsetTop, behavior: 'auto' })
    setActiveCategory(index)
  }

  useEffect(() => {
    const scroll = scroller()
    if (!scroll) return
    const onScroll = () => {
      const anchors = categoryAnchors()
      let current = 0
      anchors.forEach((anchor, i) => { if (anchor.offsetTop <= scroll.scrollTop + 8) current = i })
      setActiveCategory(current)
      if (tab === 'lucide' && scroll.scrollTop + scroll.clientHeight > scroll.scrollHeight - 200) {
        setLucideLimit(limit => Math.min(limit + LUCIDE_CHUNK, lucideMatches.length))
      }
    }
    scroll.addEventListener('scroll', onScroll, { passive: true })
    return () => scroll.removeEventListener('scroll', onScroll)
  }, [scroller, categoryAnchors, tab, lucideMatches.length])

  useEffect(() => setActiveCategory(0), [tab, query])

  /*
    **Beim Öffnen zum gewählten Icon scrollen** (gewünscht 2026-09-26): So
    lässt sich die Reihe durchprobieren, ohne jedes Mal hinunterzuscrollen.
    Einmal je Tab und nur ohne Suche; gezielt wird auf das Icon in seiner
    Kategorie, nicht auf die Kopie unter „Zuletzt benutzt“.
  */
  const scrolledFor = useRef<string | null>(null)
  useEffect(() => {
    const parsed = parseProjectIcon(value)
    if (!parsed || parsed.kind !== tab || query || scrolledFor.current === tab) return
    if (tab === 'emoji') return
    const body = bodyRef.current
    const cell = body?.querySelector<HTMLElement>('[data-picker-section]:not([data-picker-section="recent"]) .icon-picker__cell--selected')
    if (!body || !cell) return
    scrolledFor.current = tab
    body.scrollTop = Math.max(0, cell.offsetTop - body.clientHeight / 2 + cell.offsetHeight / 2)
  }, [tab, value, query, lucideLimit])


  // Emojis: „Zuletzt benutzt“ ist eine Kategorie der Liste selbst (siehe
  // `withRecentEmojis`); beim Suchen nicht — dort stünden sie doppelt.
  const recentLabel = t('iconPicker.recent')
  const selectedEmoji = parseProjectIcon(value)?.kind === 'emoji' ? parseProjectIcon(value)!.value : null

  // Ist schon ein Emoji gewählt, zählt seine Stelle in der Liste — „Zuletzt
  // benutzt“ ist für den Anfang, wenn noch keins gewählt ist (gewünscht 2026-09-26).
  const hasValueHere = parseProjectIcon(value)?.kind === tab
  const emojiResolver = useMemo(
    () => (query || tab !== 'emoji' || hasValueHere
      ? resolveBundledEmojiData
      : withRecentEmojis(resolveBundledEmojiData, recent.map(icon => icon.slice(icon.indexOf(':') + 1)), recentLabel)),
    [query, tab, recent, recentLabel, hasValueHere],
  )

  // Emojis zeichnet frimousse nur, wo gerade hingeschaut wird — die Lage des
  // gewählten wird deshalb aus den Daten gerechnet: Kategorien und Reihen zu je
  // neun, mit den gemessenen Höhen von Kopf und Reihe.
  useEffect(() => {
    const parsed = parseProjectIcon(value)
    if (tab !== 'emoji' || parsed?.kind !== 'emoji' || query || scrolledFor.current === 'emoji') return
    let cancelled = false
    let frame = 0
    void Promise.resolve(emojiResolver(language, {})).then(data => {
      const tryScroll = (attempt: number) => {
        if (cancelled) return
        const root = bodyRef.current?.closest<HTMLElement>('[frimousse-root]')
        const viewport = bodyRef.current?.querySelector<HTMLElement>('[frimousse-viewport]')
        const style = root ? getComputedStyle(root) : null
        const rowHeight = parseFloat(style?.getPropertyValue('--frimousse-row-height') ?? '')
        const headerHeight = parseFloat(style?.getPropertyValue('--frimousse-category-header-height') ?? '')
        if (!viewport || !rowHeight || !headerHeight) {
          if (attempt < 60) frame = requestAnimationFrame(() => tryScroll(attempt + 1))
          return
        }
        const top = emojiOffset(data, parsed.value, EMOJI_COLUMNS, rowHeight, headerHeight)
        if (top === null) {
          scrolledFor.current = 'emoji'
          return
        }
        const target = Math.max(0, top - viewport.clientHeight / 2 + rowHeight / 2)
        // Die Höhe der Liste steht erst, wenn frimousse die Daten verteilt hat
        // (im Leerlauf nach dem Laden) — vorher klemmte der Browser auf 0.
        if (viewport.scrollHeight - viewport.clientHeight < target && attempt < 60) {
          frame = requestAnimationFrame(() => tryScroll(attempt + 1))
          return
        }
        scrolledFor.current = 'emoji'
        viewport.scrollTop = target
      }
      tryScroll(0)
    })
    return () => {
      cancelled = true
      cancelAnimationFrame(frame)
    }
  }, [tab, value, query, emojiResolver, language])

  const recentRow = recent.length > 0 && !query && tab !== 'emoji' && !hasValueHere && (
    <section className="icon-picker__section" data-picker-section="recent" aria-label={t('iconPicker.recent')}>
      <h3 className="icon-picker__section-title">{t('iconPicker.recent')}</h3>
      <div className={`icon-picker__grid icon-picker__grid--${tab}`}>
        {/* Emojis stehen hier nie: Deren „Zuletzt benutzt" ist eine Kategorie der Liste (`withRecentEmojis`). */}
        {recent.map(icon => (
          <button key={icon} type="button" className="icon-picker__cell" onClick={() => choose(icon)} aria-label={icon.slice(icon.indexOf(':') + 1)}>
            <ProjectIcon icon={icon} iconColor={tab === 'lucide' ? shownLucideColor : null} size={tab === 'happy' ? 36 : 20} />
          </button>
        ))}
      </div>
    </section>
  )

  const bar = tab === 'happy'
    ? [
        ...(recentRow ? [{ key: 'recent', label: t('iconPicker.recent'), icon: <Clock size={16} /> }] : []),
        ...happySections.map(s => ({ key: s.id, label: s.label, icon: <RewardCategoryIcon slug={s.slug} size={16} /> })),
      ]
    : tab === 'emoji'
      ? [
          ...(recent.length > 0 && !query && !hasValueHere ? [{ key: 'recent', label: recentLabel, icon: <Clock size={16} /> }] : []),
          ...EMOJI_CATEGORIES.map(({ key, Icon }) => ({ key, label: t(`iconPicker.emojiCategories.${key}`), icon: <Icon size={16} /> })),
        ]
      : []

  const colorName = (key: string | null) => (key ? t(`titleColors.${key}`) : t('iconPicker.colorDefault'))

  return (
    <div className={`icon-picker${className ? ` ${className}` : ''}`} data-tab={tab}>
      <div className="icon-picker__header">
        <div className="icon-picker__tabs" role="tablist" aria-label={t('iconPicker.label')}>
          {TABS.map(kind => (
            <button
              key={kind}
              type="button"
              role="tab"
              aria-selected={tab === kind}
              className={`icon-picker__tab${tab === kind ? ' icon-picker__tab--selected' : ''}`}
              onClick={() => setTab(kind)}
            >
              {t(`iconPicker.tabs.${kind}`)}
            </button>
          ))}
        </div>
        <button
          type="button"
          className="icon-picker__remove"
          disabled={!value}
          onClick={() => onValueChange(null)}
        >
          <Trash2 size={16} aria-hidden="true" />
          {t('iconPicker.remove')}
        </button>
      </div>

      <div className="icon-picker__toolbar">
        <label className="icon-picker__search">
          <Search size={16} aria-hidden="true" className="icon-picker__search-icon" />
          <input
            ref={searchRef}
            type="search"
            className="icon-picker__search-input"
            placeholder={t('iconPicker.search')}
            aria-label={t('iconPicker.search')}
            value={query}
            onChange={e => setQuery(e.target.value)}
            autoFocus
          />
        </label>
        <WithTooltip content={t('iconPicker.shuffle')}>
          <button type="button" className="icon-picker__tool" onClick={shuffle} aria-label={t('iconPicker.shuffle')}>
            <Shuffle size={16} aria-hidden="true" />
          </button>
        </WithTooltip>
        {tab === 'emoji' && (
          <button
            type="button"
            className="icon-picker__tool icon-picker__tool--emoji"
            aria-label={t('iconPicker.skinTone')}
            onClick={() => {
              const index = SKIN_TONES.findIndex(s => s.tone === skinTone)
              const next = SKIN_TONES[(index + 1) % SKIN_TONES.length].tone
              setSkinToneState(next)
              setSkinTone(next)
            }}
          >
            {SKIN_TONES.find(s => s.tone === skinTone)?.hand ?? '✋'}
          </button>
        )}
        {tab === 'lucide' && (
          <button
            type="button"
            className={`icon-picker__tool${colorsOpen ? ' icon-picker__tool--open' : ''}`}
            aria-label={`${t('iconPicker.color')}: ${colorName(lucideColor)}`}
            aria-expanded={colorsOpen}
            onClick={() => setColorsOpen(open => !open)}
          >
            <span className="icon-picker__drop" data-color={lucideColor ?? undefined}>
              <ColorDrop fill="currentColor" size={16} />
            </span>
          </button>
        )}
      </div>

      {tab === 'lucide' && colorsOpen && (
        <div
          className="icon-picker__colors"
          role="group"
          aria-label={t('iconPicker.color')}
          onMouseLeave={() => { setHoverColor(undefined); onPreview?.(undefined) }}
        >
          {[null, ...TITLE_PICKER_COLORS].map(key => (
            <button
              key={key ?? 'default'}
              type="button"
              className="icon-picker__color"
              aria-label={colorName(key)}
              aria-pressed={lucideColor === key}
              onMouseEnter={() => {
                setHoverColor(key)
                if (colorsTarget) onPreview?.({ icon: colorsTarget, iconColor: key })
              }}
              onClick={() => {
                setLucideColorState(key)
                setLucideColor(key)
                if (colorsTarget) onValueChange({ icon: colorsTarget, iconColor: key })
              }}
            >
              <span className="icon-picker__drop" data-color={key ?? undefined}>
                <ColorDrop
                  fill="currentColor"
                  size={18}
                  outline={lucideColor === key ? 'currentColor' : undefined}
                />
              </span>
            </button>
          ))}
        </div>
      )}

      {tab === 'emoji' ? (
        <EmojiPicker.Root
          // frimousse liest `resolveEmojiData` nur beim Einhängen — neue Daten
          // (Verlauf, Suche an/aus) brauchen ein neues Einhängen.
          key={query ? 'search' : hasValueHere ? 'chosen' : `recent:${recent.join('|')}`}
          className="icon-picker__emoji"
          locale={language}
          columns={EMOJI_COLUMNS}
          skinTone={skinTone}
          resolveEmojiData={emojiResolver}
          onEmojiSelect={({ emoji }) => choose(formatProjectIcon({ kind: 'emoji', value: emoji }))}
        >
          {/* Die Suche steht oben im gemeinsamen Feld; frimousse bekommt sie hier gespiegelt. */}
          <EmojiPicker.Search className="icon-picker__mirror" value={query} tabIndex={-1} aria-hidden="true" readOnly />
          <div className="icon-picker__body icon-picker__body--emoji" ref={bodyRef}>
            <EmojiPicker.Viewport className="icon-picker__emoji-viewport">
              <EmojiPicker.Loading className="icon-picker__state">{t('iconPicker.loading')}</EmojiPicker.Loading>
              <EmojiPicker.Empty className="icon-picker__state">{t('iconPicker.empty')}</EmojiPicker.Empty>
              <EmojiPicker.List
                className="icon-picker__emoji-list"
                components={{
                  CategoryHeader: ({ category, ...props }) => (
                    <div {...props} className="icon-picker__section-title icon-picker__section-title--sticky">{category.label}</div>
                  ),
                  Emoji: ({ emoji, ...props }) => (
                    <button {...props} className={`icon-picker__cell icon-picker__cell--emoji${selectedEmoji && sameEmoji(emoji.emoji, selectedEmoji) ? ' icon-picker__cell--selected' : ''}`}>{emoji.emoji}</button>
                  ),
                }}
              />
            </EmojiPicker.Viewport>
          </div>
          <div className="icon-picker__footer">
            <nav className="icon-picker__categories" aria-label={t('iconPicker.categories')}>
              {bar.map((item, i) => (
                <button
                  key={item.key}
                  type="button"
                  className={`icon-picker__category${activeCategory === i ? ' icon-picker__category--active' : ''}`}
                  onClick={() => jumpToCategory(i)}
                  aria-label={item.label}
                >
                  {item.icon}
                </button>
              ))}
            </nav>
          </div>
        </EmojiPicker.Root>
      ) : (
        <>
          <div className="icon-picker__body" ref={bodyRef}>
            {recentRow}
            {tab === 'happy' && happySections.map(section => (
              <section key={section.id} className="icon-picker__section" data-picker-section={section.slug} aria-label={section.label}>
                <h3 className="icon-picker__section-title">{section.label}</h3>
                <div className="icon-picker__grid icon-picker__grid--happy">
                  {section.items.map(item => (
                    <button
                      key={item.icon}
                      type="button"
                      className={`icon-picker__cell${item.icon === value ? ' icon-picker__cell--selected' : ''}`}
                      onClick={() => choose(item.icon)}
                      aria-label={item.label}
                    >
                      <ProjectIcon icon={item.icon} size={36} />
                    </button>
                  ))}
                </div>
              </section>
            ))}
            {tab === 'lucide' && lucideMatches.length > 0 && (
              <section className="icon-picker__section" data-picker-section="icons" aria-label={t('iconPicker.icons')}>
                <h3 className="icon-picker__section-title">{t('iconPicker.icons')}</h3>
                <div className="icon-picker__grid icon-picker__grid--lucide">
                  {lucideMatches.slice(0, lucideLimit).map(name => {
                    const icon = formatProjectIcon({ kind: 'lucide', value: name })
                    return (
                      <button
                        key={name}
                        type="button"
                        className={`icon-picker__cell${icon === value ? ' icon-picker__cell--selected' : ''}`}
                        onClick={() => choose(icon)}
                        aria-label={splitCamel(name)}
                      >
                        <ProjectIcon icon={icon} iconColor={shownLucideColor} size={20} />
                      </button>
                    )
                  })}
                </div>
              </section>
            )}
            {((tab === 'happy' && happySections.length === 0) || (tab === 'lucide' && lucideMatches.length === 0)) && (
              <p className="icon-picker__state">{t('iconPicker.empty')}</p>
            )}
          </div>
          {bar.length > 0 && (
            <div className="icon-picker__footer">
              <nav className="icon-picker__categories" aria-label={t('iconPicker.categories')}>
                {bar.map((item, i) => (
                  <button
                    key={item.key}
                    type="button"
                    className={`icon-picker__category${activeCategory === i ? ' icon-picker__category--active' : ''}`}
                    onClick={() => jumpToCategory(i)}
                    aria-label={item.label}
                  >
                    {item.icon}
                  </button>
                ))}
              </nav>
            </div>
          )}
        </>
      )}
    </div>
  )
}
